import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './WithdrawalAdmin.css';

const WithdrawalAdmin = () => {
  const { token, user, currentUser, isAuthenticated, logout } = useAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [filteredWithdrawals, setFilteredWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [tokenError, setTokenError] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Check if current user is admin (to show filter) or merchant (to auto-filter)
  const isAdmin = currentUser?.role === 'admin';
  const isMerchant = currentUser?.role === 'merchant';
  const userMerchantId = currentUser?.merchant_id;

  // New withdrawal form
  const [newWithdrawalForm, setNewWithdrawalForm] = useState({
    user_id: '',
    amount: '',
    withdrawal_method: 'bank_transfer',
    account_details: {},
    admin_remarks: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    // Token ရှိမရှိနှင့် authenticated ဖြစ်မဖြစ် စစ်ဆေးခြင်း
    if (!token || !isAuthenticated) {
      setTokenError(true);
      console.error("No authentication token available or user not authenticated");
      return;
    }
    
    // Admin သို့မဟုတ် merchant role ကသာ ဝင်ရောက်ကြည့်ရှုနိုင်ရန် စစ်ဆေးခြင်း
    if (user && !['admin', 'merchant'].includes(user.role)) {
      setApiError("You do not have permission to access this page");
      return;
    }
    
    if (isAdmin) {
      fetchMerchants(); // Only fetch merchants for admin
    }
    fetchWithdrawals();
  }, [user, token, isAuthenticated, currentUser]);

  // Filter withdrawals by merchant (only for admin)
  useEffect(() => {
    if (isAdmin) {
      if (selectedMerchant === '') {
        setFilteredWithdrawals(withdrawals);
      } else {
        const filtered = withdrawals.filter(withdrawal => 
          withdrawal.merchant_id === selectedMerchant
        );
        setFilteredWithdrawals(filtered);
      }
    } else {
      // For merchants, show all withdrawals (backend already filters by merchant_id)
      setFilteredWithdrawals(withdrawals);
    }
  }, [selectedMerchant, withdrawals, isAdmin]);

  const getMerchantName = (merchant_id, merchant_name) => {
    // Use merchant_name from the API response if available
    if (merchant_name) {
      return merchant_name;
    }
    // Fallback to looking up in merchants array (for backward compatibility)
    const merchant = merchants.find(m => m.merchant_id === merchant_id);
    return merchant ? merchant.name : 'Unknown Merchant';
  };

  const handleMerchantFilter = (event) => {
    setSelectedMerchant(event.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearFilter = () => {
    setSelectedMerchant('');
    setCurrentPage(1); // Reset to first page when clearing filter
  };

  const fetchWithdrawals = async () => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      setTokenError(true);
      return;
    }
    try {
      console.log("Fetching withdrawals with token", token);
      const response = await axios.get('/withdrawals/admin/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.withdrawals) {
        setWithdrawals(response.data.withdrawals);
        // Auto-filter for merchants is handled by backend
        setFilteredWithdrawals(response.data.withdrawals);
      } else {
        console.warn("Unexpected response format:", response.data);
        setWithdrawals([]);
        setFilteredWithdrawals([]);
      }
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      
      // Handle specific error cases
      if (error.response && error.response.status === 401) {
        setTokenError(true);
        // If token is invalid, logout the user
        logout();
      } else {
        setApiError("Failed to load withdrawal data. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchants = async () => {
    try {
      const response = await axios.get('/admin/merchants', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMerchants(response.data.merchants || []);
    } catch (err) {
      console.error('Error fetching merchants:', err);
    }
  };

  const handleMerchantFilterChange = (merchantId) => {
    setSelectedMerchant(merchantId);
    if (merchantId === '') {
      setFilteredWithdrawals(withdrawals);
    } else {
      const filtered = withdrawals.filter(
        (withdrawal) => withdrawal.merchant_id === merchantId
      );
      setFilteredWithdrawals(filtered);
    }
  };

  const fetchUsers = async (search = '') => {
    if (!showCreateModal) return;
    if (!token || !isAuthenticated) {
      setTokenError(true);
      return;
    }
    setLoadingUsers(true);
    try {
      console.log("Fetching users with token:", token);
      
      // Debounce search term
      const trimmedSearch = search.trim();
      if (trimmedSearch.length < 2 && trimmedSearch.length > 0) {
        setLoadingUsers(false);
        return;
      }
      
      // For merchant users, fetch only their merchant's app users
      let endpoint = `/app-users?search=${encodeURIComponent(trimmedSearch)}`;
      if (isMerchant && userMerchantId) {
        endpoint = `/admin/app-users?merchant_id=${userMerchantId}&search=${encodeURIComponent(trimmedSearch)}`;
      }
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && (response.data.users || response.data.appUsers)) {
        // Handle different response structures
        const usersData = response.data.users || response.data.appUsers || [];
        // Process users to ensure balance is properly handled
        const processedUsers = usersData.map(user => ({
          ...user,
          // Use deposit_balance if available, otherwise try balance or default to 0
          balance: user.deposit_balance || user.balance || user.deposit || 0
        }));
        setUsers(processedUsers);
      } else {
        console.warn("Unexpected response format:", response.data);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      
      if (error.response) {
        if (error.response.status === 401) {
          setTokenError(true);
        } else {
          setApiError("Failed to load user data. Please try again.");
        }
      } else {
        setApiError("Network error. Please check your connection.");
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  // Call this when opening the create modal
  const handleOpenCreateModal = () => {
    if (!token || !isAuthenticated) {
      alert("Authentication error: Please log in again.");
      logout();
      return;
    }

    setShowCreateModal(true);
    fetchUsers();
    resetForm();
  };

  const resetForm = () => {
    // Set different default values based on user role
    const defaultForm = {
      user_id: '',
      amount: '',
      withdrawal_method: isMerchant ? 'merchant' : 'bank_transfer', // Default to 'merchant' for merchant users
      account_details: isMerchant ? { details: 'merchant' } : {}, // Default to 'merchant' for merchant users
      admin_remarks: ''
    };

    setNewWithdrawalForm(defaultForm);
    setFormErrors({});
    setSelectedUser(null);
  };

  const handleUserSearchChange = (e) => {
    const value = e.target.value;
    setUserSearchTerm(value);

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      fetchUsers(value);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  // User ကို select လုပ်ခြင်း function ကို ပြင်ဆင်ခြင်း
  const selectUser = (user) => {
    setSelectedUser(user);
    
    const updatedForm = {
      ...newWithdrawalForm,
      user_id: user.id
    };

    // For merchant role, automatically set amount to user's full balance
    if (isMerchant) {
      const userBalance = parseFloat(user.balance || user.deposit_balance || 0);
      updatedForm.amount = userBalance.toString();
    }

    setNewWithdrawalForm(updatedForm);
    
    // Clear user_id error if it exists
    if (formErrors.user_id) {
      setFormErrors({
        ...formErrors,
        user_id: undefined
      });
    }
  };

  const handleInputChange = (field, value) => {
    setNewWithdrawalForm({
      ...newWithdrawalForm,
      [field]: value
    });

    // Clear field error if it exists
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: undefined
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!newWithdrawalForm.user_id) {
      errors.user_id = 'Please select a user';
    }

    if (!newWithdrawalForm.amount || parseFloat(newWithdrawalForm.amount) <= 0) {
      errors.amount = 'Please enter a valid amount';
    }

    if (selectedUser && parseFloat(newWithdrawalForm.amount) > parseFloat(selectedUser.balance || 0)) {
      errors.amount = `Amount cannot exceed user's balance: ¥${parseFloat(selectedUser.balance || 0).toFixed(0)}`;
    }

    // Only validate withdrawal method for admin users
    if (isAdmin && !newWithdrawalForm.withdrawal_method) {
      errors.withdrawal_method = 'Please select a withdrawal method';
    }

    // Only validate account details for admin users
    if (isAdmin && (!newWithdrawalForm.account_details || 
        Object.keys(newWithdrawalForm.account_details).length === 0 || 
        !newWithdrawalForm.account_details.details)) {
      errors.account_details = 'Please enter account details';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateWithdrawal = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await axios.post('/withdrawals/admin/create', newWithdrawalForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Withdrawal record added successfully!');
      setShowCreateModal(false);
      fetchWithdrawals(); // Refresh the list
      resetForm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error creating withdrawal record';
      alert(errorMessage);
    }
  };

  const handleAccountDetailsChange = (value) => {
    setNewWithdrawalForm({
      ...newWithdrawalForm,
      account_details: {
        ...newWithdrawalForm.account_details,
        details: value
      }
    });

    // Clear specific field error
    if (formErrors.account_details) {
      setFormErrors({
        ...formErrors,
        account_details: undefined
      });
    }
  };

  // Filter and search logic
  const searchedWithdrawals = filteredWithdrawals.filter(withdrawal => {
    return searchTerm === '' ||
      withdrawal.id?.toString().includes(searchTerm) ||
      withdrawal.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      withdrawal.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      withdrawal.amount?.toString().includes(searchTerm);
  });

  // Pagination
  const totalPages = Math.ceil(searchedWithdrawals.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedWithdrawals = searchedWithdrawals.slice(startIndex, startIndex + itemsPerPage);

  // Statistics
  const totalAmount = withdrawals.reduce((sum, w) => sum + parseFloat(w.amount || 0), 0);

  // Token ပြဿနာအတွက် warning ပြသခြင်း
  if (tokenError) {
    return (
      <div className="withdrawal-container">
        <div className="disabled-warning">
          <div className="disabled-warning-icon">⚠️</div>
          <div className="disabled-warning-text">
            Authentication error. Please log out and log in again to refresh your session.
          </div>
        </div>
      </div>
    );
  }

  // API error ပြသခြင်း
  if (apiError) {
    return (
      <div className="withdrawal-container">
        <div className="disabled-warning">
          <div className="disabled-warning-icon">⚠️</div>
          <div className="disabled-warning-text">
            {apiError}
          </div>
        </div>
      </div>
    );
  }

  // Admin သို့မဟုတ် merchant role မဟုတ်ပါက ဝင်ခွင့်မရှိကြောင်း ပြသခြင်း
  if (user && !['admin', 'merchant'].includes(user.role)) {
    return (
      <div className="withdrawal-container">
        <div className="disabled-warning">
          <div className="disabled-warning-icon">⚠️</div>
          <div className="disabled-warning-text">
            You do not have permission to access this page. Only administrators and merchants can manage deposit withdrawals.
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="withdrawal-container">
      {/* Header */}
      <div className="withdrawal-header">
        <h2 className="withdrawal-title">
          Deposit Withdrawals {isMerchant && currentUser?.merchant_name ? `(${currentUser.merchant_name})` : ''}
        </h2>
        <p className="withdrawal-subtitle">Record user deposit withdrawals</p>
        <button
          onClick={handleOpenCreateModal}
          className="btn btn-primary"
          style={{ marginTop: '15px' }}
        >
          Record New Withdrawal
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-blue">
              <span>📊</span>
            </div>
            <div className="stat-info">
              <p className="stat-label">Total Withdrawals</p>
              <p className="stat-value">{filteredWithdrawals.length}</p>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-green">
              <span>💵</span>
            </div>
            <div className="stat-info">
              <p className="stat-label">Total Amount</p>
              <p className="stat-value">¥{filteredWithdrawals.reduce((sum, w) => sum + parseFloat(w.amount || 0), 0).toFixed(0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Section - Only show for admin */}
      {isAdmin && (
        <div className="filters-container" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>🔍</span>
              <label className="filter-label">Filter by Merchant:</label>
            </div>
            <select
              value={selectedMerchant}
              onChange={handleMerchantFilter}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
            >
              <option value="">All Merchants</option>
              {merchants.map((merchant) => (
                <option key={merchant.merchant_id} value={merchant.merchant_id}>
                  {merchant.name}
                </option>
              ))}
            </select>
            {selectedMerchant && (
              <button
                onClick={clearFilter}
                style={{ padding: '6px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Clear Filter ✕
              </button>
            )}
            <span style={{ color: '#666', fontSize: '14px' }}>
              Showing {filteredWithdrawals.length} of {withdrawals.length} withdrawals
            </span>
          </div>
        </div>
      )}

      {/* Show count info for merchants */}
      {isMerchant && (
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
          <span style={{ color: '#1976d2', fontSize: '14px' }}>
            Showing {filteredWithdrawals.length} withdrawal records for your merchant account
          </span>
        </div>
      )}

      {/* Search */}
      <div className="filters-container">
        <div className="search-container">
          <label className="filter-label">Search</label>
          <input
            type="text"
            placeholder="Search by ID, user, email, amount..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
        </div>
      </div>

      {/* Withdrawals Table */}
      <div className="withdrawals-table-container">
        {paginatedWithdrawals.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔍</div>
            <h3 className="empty-state-title">No withdrawal records found</h3>
            <p className="empty-state-message">Start by recording a new withdrawal.</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="withdrawals-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Date</th>
                    <th>User</th>
                    <th>Amount</th>
                    {/* Only show Method column for admin */}
                    {isAdmin && <th>Method</th>}
                    {/* Only show Account Details column for admin */}
                    {isAdmin && <th>Account Details</th>}
                    {/* Only show Merchant column for admin */}
                    {isAdmin && <th>Merchant</th>}
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedWithdrawals.map((withdrawal) => (
                    <tr key={withdrawal.id}>
                      <td>
                        <div className="request-id">#{withdrawal.id}</div>
                      </td>
                      <td>
                        {new Date(withdrawal.created_at || withdrawal.requested_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="user-profile">
                          <div className="user-avatar">
                            <span className="user-avatar-text">
                              {withdrawal.username?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="user-info">
                            <div className="user-name">{withdrawal.username}</div>
                            <div className="user-email">{withdrawal.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="amount">¥{parseFloat(withdrawal.amount || 0).toFixed(0)}</div>
                      </td>
                      {/* Only show Method cell for admin */}
                      {isAdmin && (
                        <td>
                          <div className="method-name">
                            {(withdrawal.withdrawal_method || 'unknown').replace('_', ' ')}
                          </div>
                        </td>
                      )}
                      {/* Only show Account Details cell for admin */}
                      {isAdmin && (
                        <td>
                          <div className="account-details">
                            <div className="account-detail-primary">
                              {withdrawal.account_details?.details || "N/A"}
                            </div>
                          </div>
                        </td>
                      )}
                      {/* Only show Merchant cell for admin */}
                      {isAdmin && (
                        <td>
                          <div style={{ 
                            display: 'inline-block', 
                            padding: '4px 8px', 
                            backgroundColor: '#f0f0f0', 
                            border: '1px solid #ddd', 
                            borderRadius: '12px', 
                            fontSize: '12px',
                            color: '#555'
                          }}>
                            {getMerchantName(withdrawal.merchant_id, withdrawal.merchant_name)}
                          </div>
                        </td>
                      )}
                      <td>
                        {withdrawal.admin_remarks || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing <span className="pagination-info-highlight">{startIndex + 1}</span> to{' '}
                  <span className="pagination-info-highlight">
                    {Math.min(startIndex + itemsPerPage, filteredWithdrawals.length)}
                  </span>{' '}
                  of <span className="pagination-info-highlight">{filteredWithdrawals.length}</span> results
                </div>
                <div className="pagination-controls">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="pagination-button"
                  >
                    Previous
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`pagination-button ${currentPage === i + 1 ? 'active' : ''}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Withdrawal Modal - User Select ပြဿနာ ပြင်ဆင်ခြင်း */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">
                Record User Deposit Withdrawal
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="modal-close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-section">
              {/* User Selection - ပြင်ဆင်ထားသော User Selection အပိုင်း */}
              <div className="form-group">
                <label className="form-label">Select User *</label>
                <div style={{ marginBottom: '8px' }}>
                  <input
                    type="text"
                    placeholder="Search users by name or email..."
                    value={userSearchTerm}
                    onChange={handleUserSearchChange}
                    className="form-input"
                  />
                </div>
                <div className="user-list-container">
                  {loadingUsers ? (
                    <div className="user-list-loading">Loading users...</div>
                  ) : users.length > 0 ? (
                    <div>
                      {users.map(user => (
                        <div
                          key={user.id}
                          className={`user-list-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                          onClick={() => selectUser(user)}
                        >
                          <div className="user-item-content">
                            <div className="user-avatar">
                              <span className="user-avatar-text">
                                {user.username?.charAt(0).toUpperCase() || 'U'}
                              </span>
                            </div>
                            <div className="user-info">
                              <div className="user-name">{user.username}</div>
                              <div className="user-email">{user.email}</div>
                            </div>
                            <div className="user-balance">
                              Balance: ¥{parseFloat(user.balance || user.deposit_balance || 0).toFixed(0)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="user-list-empty">
                      {userSearchTerm ? "No users found matching your search" : "Type to search for users"}
                    </div>
                  )}
                </div>
                {formErrors.user_id && (
                  <p className="form-error">{formErrors.user_id}</p>
                )}

                {selectedUser && (
                  <div className="selected-user-info">
                    <p>Selected: <strong>{selectedUser.username}</strong> (Balance: ¥{parseFloat(selectedUser.balance || selectedUser.deposit_balance || 0).toFixed(0)})</p>
                  </div>
                )}
              </div>

              {/* Amount */}
              <div className="form-group">
                <label className="form-label">Withdrawal Amount *</label>
                <div className="amount-input-group">
                  <span className="amount-input-prefix">¥</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newWithdrawalForm.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    className={`form-input amount-input ${formErrors.amount ? 'has-error' : ''}`}
                    placeholder="0"
                    readOnly={isMerchant} // Make read-only for merchant users
                  />
                </div>
                {selectedUser && (
                  <div className="form-hint">
                    <span>Available: ¥{parseFloat(selectedUser.balance || 0).toFixed(0)}</span>
                    {isMerchant && <span> (Full balance will be withdrawn)</span>}
                  </div>
                )}
                {formErrors.amount && (
                  <p className="form-error">{formErrors.amount}</p>
                )}
              </div>

              {/* Withdrawal Method - Only show for admin */}
              {isAdmin && (
                <div className="form-group">
                  <label className="form-label">Withdrawal Method *</label>
                  <select
                    value={newWithdrawalForm.withdrawal_method}
                    onChange={(e) => handleInputChange('withdrawal_method', e.target.value)}
                    className={`form-select ${formErrors.withdrawal_method ? 'has-error' : ''}`}
                  >
                    <option value="">Select Method</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="cash">Cash</option>
                    <option value="other">Other</option>
                  </select>
                  {formErrors.withdrawal_method && (
                    <p className="form-error">{formErrors.withdrawal_method}</p>
                  )}
                </div>
              )}

              {/* Account Details - Only show for admin */}
              {isAdmin && (
                <div className="form-group">
                  <label className="form-label">Account/Payment Details *</label>
                  <textarea
                    value={newWithdrawalForm.account_details?.details || ''}
                    onChange={(e) => handleAccountDetailsChange(e.target.value)}
                    rows="3"
                    className={`form-textarea ${formErrors.account_details ? 'has-error' : ''}`}
                    placeholder="Enter payment details (e.g., bank account, transaction reference)"
                  />
                  {formErrors.account_details && (
                    <p className="form-error">{formErrors.account_details}</p>
                  )}
                </div>
              )}

              {/* Admin Remarks */}
              <div className="form-group">
                <label className="form-label">Admin Remarks</label>
                <textarea
                  value={newWithdrawalForm.admin_remarks}
                  onChange={(e) => handleInputChange('admin_remarks', e.target.value)}
                  rows="2"
                  className="form-textarea"
                  placeholder="Optional notes about this withdrawal"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateWithdrawal}
                className="btn btn-primary"
              >
                Save Withdrawal Record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalAdmin;